import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { seriesAPI, showAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Trash, Trophy, Calendar, MapPin, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SeriesDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('standings');
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableShows, setAvailableShows] = useState([]);
  const [showEditPoints, setShowEditPoints] = useState(false);
  const [editPoints, setEditPoints] = useState({});

  const userRoles = user?.roles || [user?.role || ''];
  const isProducer = userRoles.includes('ORG_ADMIN') || userRoles.includes('MASTER');

  const fetchData = useCallback(async () => {
    try {
      const [seriesRes, standingsRes] = await Promise.all([
        seriesAPI.get(id),
        seriesAPI.getStandings(id)
      ]);
      setSeries(seriesRes.data);
      setStandings(standingsRes.data);
      setEditPoints(seriesRes.data.points_table || {});
    } catch {
      toast.error('Failed to load series');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddShow = async () => {
    try {
      const { data: shows } = await showAPI.list('ALL');
      const existingIds = new Set(series.shows.map(s => s.id));
      setAvailableShows(shows.filter(s => !existingIds.has(s.id)));
      setShowAddModal(true);
    } catch {
      toast.error('Failed to load shows');
    }
  };

  const addShowToSeries = async (showId) => {
    try {
      await seriesAPI.addShow(id, showId);
      toast.success('Show added to series');
      setShowAddModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add show');
    }
  };

  const removeShowFromSeries = async (showId) => {
    try {
      await seriesAPI.removeShow(id, showId);
      toast.success('Show removed from series');
      fetchData();
    } catch {
      toast.error('Failed to remove show');
    }
  };

  const savePoints = async () => {
    try {
      await seriesAPI.update(id, { points_table: editPoints });
      toast.success('Points table updated');
      setShowEditPoints(false);
      fetchData();
    } catch {
      toast.error('Failed to update points');
    }
  };

  const toggleStatus = async () => {
    const newStatus = series.status === 'ACTIVE' ? 'COMPLETED' : 'ACTIVE';
    try {
      await seriesAPI.update(id, { status: newStatus });
      toast.success(`Series marked as ${newStatus.toLowerCase()}`);
      fetchData();
    } catch {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#9E9E9E]">Loading series...</div>
      </div>
    );
  }

  if (!series) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#FF3B30]">Series not found</div>
      </div>
    );
  }

  const pointsEntries = Object.entries(series.points_table || {}).sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8 px-4" data-testid="series-detail-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <button
              onClick={() => navigate(isProducer ? '/series' : '/shows')}
              className="inline-flex items-center gap-1 text-[#9E9E9E] hover:text-[#0096FF] text-sm mb-3 transition-colors"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <h1 className="text-3xl sm:text-4xl font-black text-white" style={{fontFamily: 'Outfit'}} data-testid="series-name">
              {series.name}
            </h1>
            {series.description && <p className="text-[#9E9E9E] mt-1">{series.description}</p>}
            {series.org_name && <p className="text-[#0096FF] text-sm mt-1">by {series.org_name}</p>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              series.status === 'ACTIVE' ? 'bg-[#34C759]/20 text-[#34C759]' : 'bg-[#9E9E9E]/20 text-[#9E9E9E]'
            }`} data-testid="series-status">
              {series.status}
            </span>
            {isProducer && (
              <button
                onClick={toggleStatus}
                data-testid="toggle-status-btn"
                className="px-3 py-1 text-xs border border-[#262626] hover:border-[#0096FF] text-[#9E9E9E] hover:text-white rounded-full transition-colors"
              >
                {series.status === 'ACTIVE' ? 'Mark Complete' : 'Reactivate'}
              </button>
            )}
          </div>
        </div>

        {/* Points Table Summary */}
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#9E9E9E]">Points Per Place</h3>
            {isProducer && (
              <button
                onClick={() => setShowEditPoints(!showEditPoints)}
                data-testid="edit-points-btn"
                className="text-xs text-[#0096FF] hover:text-[#00D4FF] transition-colors"
              >
                {showEditPoints ? 'Cancel' : 'Edit'}
              </button>
            )}
          </div>
          {showEditPoints ? (
            <div>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mb-3">
                {Object.entries(editPoints).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).map(([place, pts]) => (
                  <div key={place} className="flex flex-col items-center">
                    <span className="text-xs text-[#9E9E9E] mb-1">{place}{place === '1' ? 'st' : place === '2' ? 'nd' : place === '3' ? 'rd' : 'th'}</span>
                    <input
                      type="number"
                      min="0"
                      value={pts}
                      onChange={(e) => setEditPoints({...editPoints, [place]: parseInt(e.target.value) || 0})}
                      className="w-full px-1 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-white text-center text-sm focus:outline-none focus:border-[#0096FF]"
                    />
                  </div>
                ))}
              </div>
              <button onClick={savePoints} data-testid="save-points-btn" className="px-4 py-2 bg-[#0096FF] hover:bg-[#00D4FF] text-white text-sm font-semibold rounded-md transition-colors">
                Save Points
              </button>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              {pointsEntries.map(([place, pts]) => (
                <div key={place} className="flex items-center gap-1 px-3 py-1.5 bg-[#0A0A0A] rounded text-sm">
                  <span className="text-[#9E9E9E]">{place}{place === '1' ? 'st' : place === '2' ? 'nd' : place === '3' ? 'rd' : 'th'}:</span>
                  <span className="text-white font-bold">{pts}pts</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#161616] p-1 rounded-lg mb-6 w-fit" data-testid="series-tabs">
          <button
            onClick={() => setActiveTab('standings')}
            data-testid="standings-tab"
            className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'standings' ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white'}`}
          >
            Standings
          </button>
          <button
            onClick={() => setActiveTab('shows')}
            data-testid="shows-tab"
            className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'shows' ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white'}`}
          >
            Shows ({series.shows.length})
          </button>
        </div>

        {/* Standings Tab */}
        {activeTab === 'standings' && standings && (
          <div>
            {standings.standings.length === 0 ? (
              <div className="bg-[#161616] border border-[#262626] rounded-lg p-12 text-center">
                <Trophy size={48} className="text-[#262626] mx-auto mb-4" />
                <p className="text-[#9E9E9E]">No standings yet</p>
                <p className="text-[#525252] text-sm mt-1">Add shows with scored runs to see cumulative standings</p>
              </div>
            ) : (
              <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden">
                <table className="w-full" data-testid="standings-table">
                  <thead>
                    <tr className="bg-[#0A0A0A]">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide w-16">Rank</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Rider</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Shows</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.standings.map((rider) => {
                      const rankColors = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };
                      const rankColor = rankColors[rider.rank] || '#FFFFFF';
                      return (
                        <tr key={rider.rider_id || rider.rider_name} className="border-t border-[#262626] hover:bg-[#0A0A0A]/50 transition-colors" data-testid={`standing-row-${rider.rank}`}>
                          <td className="px-4 py-3">
                            <span className="text-lg font-black" style={{color: rankColor, fontFamily: 'Outfit'}}>
                              {rider.rank}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-white font-semibold">{rider.rider_name}</p>
                            {rider.class_results && rider.class_results.length > 0 && (
                              <p className="text-[#9E9E9E] text-xs mt-0.5">
                                Best: {rider.class_results[0].class_name} ({rider.class_results[0].show_name}) — {rider.class_results[0].place}{rider.class_results[0].place === 1 ? 'st' : rider.class_results[0].place === 2 ? 'nd' : rider.class_results[0].place === 3 ? 'rd' : 'th'}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-[#9E9E9E]">{rider.shows_entered}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-[#0096FF] font-bold text-lg" style={{fontFamily: 'Outfit'}}>{rider.total_points}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Per-Show Breakdown */}
            {standings.show_results && standings.show_results.length > 0 && (
              <div className="mt-6 space-y-4">
                <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>Per-Show Results</h3>
                {standings.show_results.map((sr) => (
                  <div key={sr.show_id || sr.show_name} className="bg-[#161616] border border-[#262626] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-white font-semibold">{sr.show_name}</h4>
                        <p className="text-[#9E9E9E] text-xs">{sr.show_date}</p>
                      </div>
                      <button
                        onClick={() => navigate(`/shows/${sr.show_id}`)}
                        className="text-xs text-[#0096FF] hover:text-[#00D4FF] transition-colors"
                      >
                        View Show
                      </button>
                    </div>
                    {sr.top_riders.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {sr.top_riders.map((r) => (
                          <div key={r.rider_id || r.rider_name} className="px-3 py-2 bg-[#0A0A0A] rounded-lg text-sm">
                            <span className="text-[#9E9E9E]">{r.rider_name}</span>
                            <span className="text-[#0096FF] font-bold ml-2">{r.points}pts</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#525252] text-sm">No scored results yet</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Shows Tab */}
        {activeTab === 'shows' && (
          <div>
            {isProducer && (
              <button
                onClick={handleAddShow}
                data-testid="add-show-to-series-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors text-sm mb-4"
              >
                <Plus size={16} weight="bold" /> Add Show
              </button>
            )}

            {series.shows.length === 0 ? (
              <div className="bg-[#161616] border border-[#262626] rounded-lg p-12 text-center">
                <Calendar size={48} className="text-[#262626] mx-auto mb-4" />
                <p className="text-[#9E9E9E]">No shows added yet</p>
                <p className="text-[#525252] text-sm mt-1">Add shows from your organization to track cumulative points</p>
              </div>
            ) : (
              <div className="space-y-3">
                {series.shows.map(show => (
                  <div key={show.id} className="bg-[#161616] border border-[#262626] rounded-lg p-4 flex items-center justify-between" data-testid={`series-show-${show.id}`}>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/shows/${show.id}`)}>
                      <h4 className="text-white font-semibold hover:text-[#0096FF] transition-colors">{show.name}</h4>
                      <div className="flex items-center gap-3 mt-1 text-sm text-[#9E9E9E]">
                        <span className="flex items-center gap-1"><Calendar size={12} /> {show.start_date}</span>
                        <span className="flex items-center gap-1"><MapPin size={12} /> {show.location}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          show.state === 'COMPLETED' ? 'bg-[#34C759]/20 text-[#34C759]' :
                          show.state === 'LIVE' ? 'bg-[#FF9500]/20 text-[#FF9500]' :
                          'bg-[#0096FF]/20 text-[#0096FF]'
                        }`}>{show.state}</span>
                      </div>
                    </div>
                    {isProducer && (
                      <button
                        onClick={() => removeShowFromSeries(show.id)}
                        data-testid={`remove-show-${show.id}`}
                        className="ml-3 p-2 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded transition-colors"
                        title="Remove from series"
                      >
                        <Trash size={18} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add Show Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
            <div className="bg-[#161616] border border-[#262626] rounded-lg w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-show-modal">
              <div className="p-6">
                <h2 className="text-xl font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>Add Show to Series</h2>
                {availableShows.length === 0 ? (
                  <p className="text-[#9E9E9E] text-center py-8">No available shows to add</p>
                ) : (
                  <div className="space-y-2">
                    {availableShows.map(show => (
                      <button
                        key={show.id}
                        onClick={() => addShowToSeries(show.id)}
                        data-testid={`add-show-option-${show.id}`}
                        className="w-full text-left bg-[#0A0A0A] hover:bg-[#0096FF]/10 border border-[#262626] hover:border-[#0096FF]/50 rounded-lg p-4 transition-all"
                      >
                        <p className="text-white font-semibold">{show.name}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-[#9E9E9E]">
                          <span>{show.start_date}</span>
                          <span>{show.location}</span>
                          <span className={`px-2 py-0.5 rounded font-semibold ${
                            show.state === 'COMPLETED' ? 'bg-[#34C759]/20 text-[#34C759]' :
                            show.state === 'LIVE' ? 'bg-[#FF9500]/20 text-[#FF9500]' :
                            'bg-[#0096FF]/20 text-[#0096FF]'
                          }`}>{show.state}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setShowAddModal(false)}
                  className="mt-4 w-full px-4 py-2 bg-transparent border border-[#262626] hover:border-[#0096FF] text-white rounded-md transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeriesDetail;
